import React, { useMemo, useState } from 'react';
import { Alert, Box, CircularProgress, IconButton, Tooltip, Typography } from '@mui/material';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { DEEP_GREEN, MUTED } from '../colorTokens';
import { buildReport, buildScope, emptyReport } from './incidentRemediationReport';
import { PAGE_SCOPE_NOTE } from './reportCopy';
import { useIncidentRemediation } from './useIncidentRemediation';
import { downloadPresentation } from './exportPresentation';
import { FlowSummary, MajorDefinition, MonthDetails, MonthPicker, OpenSummary, ProblemTable, TrendChart } from './ReportSections';

/** The monthly Incident Remediation report. Fetching lives in the hook and the calculation in the report module. */
const MonthlyReport = () => {
  const { data, loading, error } = useIncidentRemediation();
  const [selectedMonth, setSelectedMonth] = useState('');
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState('');

  // A start date the server sends that cannot be read must show as a message, not crash the page.
  const { report, reportError } = useMemo(() => {
    if (!data) return { report: emptyReport, reportError: '' };
    try {
      return { report: buildReport(data.problems, { reportStart: data.reportStart }), reportError: '' };
    } catch (err) {
      return { report: emptyReport, reportError: err.message };
    }
  }, [data]);
  const failure = error || reportError;

  const activeKey = report.groups.some((group) => group.key === selectedMonth)
    ? selectedMonth
    : (report.groups[0] && report.groups[0].key) || '';
  const active = report.groups.find((group) => group.key === activeKey);

  // The report scoped to the selected month: derived from report + activeKey only, so it is memoized
  // instead of recomputed on every render (e.g. when unrelated state changes).
  const { scope, averageClosedDays, closedForAverageCount, stillOpenFacts } = useMemo(
    () => buildScope(report, activeKey),
    [report, activeKey]
  );

  const ready = !loading && !failure;

  const handleDownload = async () => {
    setExporting(true);
    setExportError('');
    try {
      await downloadPresentation({ active, scope, averageClosedDays, closedForAverageCount, stillOpenFacts, rows: active.rows });
    } catch (err) {
      setExportError(`Could not create the PowerPoint: ${err.message}`);
    } finally {
      setExporting(false);
    }
  };

  return (
    <Box sx={{ mt: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, mb: 2 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: DEEP_GREEN }}>
            Monthly report
          </Typography>
          <Typography sx={{ fontSize: '0.8rem', lineHeight: 1.4, color: MUTED, mt: 0.5, maxWidth: 560 }}>
            {PAGE_SCOPE_NOTE}
          </Typography>
        </Box>
        {active && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0 }}>
            <Tooltip title="Download this month as PowerPoint">
              <span>
                <IconButton
                  size="small"
                  aria-label="Download PowerPoint"
                  disabled={exporting}
                  onClick={handleDownload}
                  sx={{ color: DEEP_GREEN, border: '1px solid rgba(26,46,34,0.3)', borderRadius: '6px', p: 0.6 }}
                >
                  {exporting ? <CircularProgress size={16} sx={{ color: DEEP_GREEN }} /> : <FileDownloadOutlinedIcon sx={{ fontSize: 18 }} />}
                </IconButton>
              </span>
            </Tooltip>
            <MonthPicker groups={report.groups} activeKey={activeKey} active={active} onChange={setSelectedMonth} />
          </Box>
        )}
      </Box>

      {loading && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, color: MUTED, py: 2 }}>
          <CircularProgress size={18} sx={{ color: DEEP_GREEN }} />
          <Typography sx={{ fontSize: '0.875rem' }}>Loading problems…</Typography>
        </Box>
      )}

      {failure && <Alert severity="error" sx={{ maxWidth: 640 }}>{failure}</Alert>}
      {exportError && <Alert severity="error" sx={{ maxWidth: 640, mb: 2 }}>{exportError}</Alert>}

      {ready && !active && data && (
        <Typography sx={{ color: MUTED, fontSize: '0.875rem' }}>
          {`No synced problems since ${new Date(data.reportStart).getUTCFullYear()} yet.`}
        </Typography>
      )}

      {ready && active && (
        <>
          <OpenSummary facts={stillOpenFacts} />
          <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start', flexWrap: 'wrap', mb: 2 }}>
            <FlowSummary scope={scope} active={active} />
            <TrendChart
              scope={scope}
              averageClosedDays={averageClosedDays}
              closedForAverageCount={closedForAverageCount}
              closedThisMonth={active.closedThisMonth}
              monthLabel={active.label}
            />
          </Box>
          <MajorDefinition />

          <MonthDetails active={active} stillOpenFacts={stillOpenFacts} />
          <ProblemTable rows={active.rows} />
        </>
      )}
    </Box>
  );
};

export default MonthlyReport;
